import { Injectable, NgZone } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Reference-counted wrapper around NgxSpinnerService so overlapping
 * show()/hide() calls (e.g. concurrent HTTP requests) can't hide the
 * spinner while other callers still expect it to be visible.
 */
@Injectable({ providedIn: 'root' })
export class SpinnerService {
  private activeRequests = 0;
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  readonly loading$: Observable<boolean> = this.loadingSubject.asObservable();
  // Watchdog: if the spinner remains active longer than this, automatically reset.
  private watchdogTimer: any = null;
  private watchdogTimeoutMs = 30000; // default 30s

  constructor(private ngxSpinnerService: NgxSpinnerService, private ngZone: NgZone) {}

  show(): void {
    this.activeRequests++;
    // Ensure change detection runs when updating loading state
    if (this.activeRequests === 1) {
      this.ngZone.run(() => {
        this.loadingSubject.next(true);
      });
      try {
        this.ngxSpinnerService.show();
      } catch (e) {
        console.warn('NgxSpinnerService.show() failed', e);
      }
    }
    // (Re)start watchdog whenever the spinner is shown so long-running visibility can be detected.
    this.startWatchdogIfNeeded();
    // Debug log
    // eslint-disable-next-line no-console
    console.debug('[SpinnerService] show -> activeRequests=', this.activeRequests);
  }

  hide(): void {
    if (this.activeRequests === 0) {
      // Debug unexpected hide
      // eslint-disable-next-line no-console
      console.warn('[SpinnerService] hide called but activeRequests is already 0');
      return;
    }
    this.activeRequests--;
    // Debug log
    // eslint-disable-next-line no-console
    console.debug('[SpinnerService] hide -> activeRequests=', this.activeRequests);
    if (this.activeRequests === 0) {
      this.ngZone.run(() => {
        this.loadingSubject.next(false);
      });
      try {
        this.ngxSpinnerService.hide();
      } catch (e) {
        console.warn('NgxSpinnerService.hide() failed', e);
      }
      // clear watchdog when spinner fully hidden
      this.clearWatchdog();
    }
  }

  /** Force-clears the counter, e.g. on logout/route reset, in case of a leak. */
  reset(): void {
    this.activeRequests = 0;
    this.loadingSubject.next(false);
    try {
      this.ngxSpinnerService.hide();
    } catch (e) {
      console.warn('NgxSpinnerService.hide() failed during reset', e);
    }
    this.clearWatchdog();
  }

  /** Configure watchdog timeout (ms). Set to 0 to disable. */
  setWatchdogTimeout(ms: number): void {
    this.watchdogTimeoutMs = Math.max(0, Math.floor(ms));
    // restart watchdog if spinner is currently visible
    if (this.activeRequests > 0) {
      this.startWatchdogIfNeeded();
    }
  }

  private startWatchdogIfNeeded(): void {
    // disabled when set to 0
    if (!this.watchdogTimeoutMs || this.watchdogTimeoutMs <= 0) return;
    // restart timer so repeated show() calls extend the watchdog
    this.clearWatchdog();
    this.watchdogTimer = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn('[SpinnerService] watchdog timeout fired — resetting spinner');
      try {
        this.ngZone.run(() => this.reset());
      } catch (e) {
        // fallback: call reset outside zone if run() fails
        try {
          this.reset();
        } catch (err) {
          // final fallback, log and ignore
          // eslint-disable-next-line no-console
          console.error('[SpinnerService] watchdog reset failed', err);
        }
      }
    }, this.watchdogTimeoutMs);
  }

  private clearWatchdog(): void {
    if (this.watchdogTimer) {
      clearTimeout(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }
}
